/**
 * @fileoverview Payment API Client (Contract-First Design)
 * @module api-client/clients/payment
 *
 * 100% type-safe Payment API client generated from contract.
 */

import {
	PAYMENT_API_CONTRACT,
	type CreatePaymentIntentPayload,
	type VerifyPaymentPayload,
	type ProcessPaymentPayload,
} from '@monorepo/types';
import { ContractClient } from '../core/contract-client';
import type { HttpClient } from '../core/http-client';

/**
 * Payment API Client
 *
 * Contract-first client for payment operations.
 * Handles Stripe payment intents, verification, and processing.
 *
 * @example
 * ```typescript
 * const paymentClient = new PaymentClient(httpClient);
 *
 * // Create payment intent
 * const intent = await paymentClient.createIntent({
 *   testId: 'test-123',
 *   customerEmail: 'user@example.com',
 *   analysisType: PaymentTier.PREMIUM
 * });
 *
 * // Verify payment
 * const verified = await paymentClient.verifyPayment({
 *   paymentIntentId: intent.paymentIntentId
 * });
 *
 * // Process payment completion
 * const result = await paymentClient.processPayment({
 *   paymentIntentId: intent.paymentIntentId,
 *   customerEmail: 'user@example.com',
 *   testData: { ... }
 * });
 * ```
 */
export class PaymentClient extends ContractClient<typeof PAYMENT_API_CONTRACT> {
	constructor(httpClient: HttpClient) {
		super(httpClient, PAYMENT_API_CONTRACT, {
			validateResponses: true, // ✅ ENABLED: Validate responses against contract
			validateRequests: true, // ✅ ENABLED: Validate requests before sending
			throwOnValidationError: true, // Throw detailed errors on validation failure
		});
	}

	/**
	 * Create a payment intent for premium analysis
	 * POST /payment/create-intent
	 * @returns Promise<PaymentIntentApiResponse> - Wrapped response: { success, data: PaymentIntentResponse, timestamp }
	 */
	async createIntent(payload: CreatePaymentIntentPayload) {
		return this.execute('createIntent', { body: payload });
	}

	/**
	 * Verify if a payment has been completed
	 * POST /payment/verify
	 * @returns Promise<VerifyPaymentApiResponse> - Wrapped response: { success, data: VerifyPaymentResponse, timestamp }
	 */
	async verifyPayment(payload: VerifyPaymentPayload) {
		return this.execute('verify', { body: payload });
	}

	/**
	 * Process payment completion (create user, link session, submit test)
	 * POST /payment/process
	 * @returns Promise<ProcessPaymentApiResponse> - Wrapped response: { success, data: ProcessPaymentResponse, timestamp }
	 */
	async processPayment(payload: ProcessPaymentPayload) {
		return this.execute('process', { body: payload });
	}

	/**
	 * Handle Stripe webhook events
	 * POST /payment/webhook
	 * @returns Promise<WebhookEventApiResponse> - Wrapped response: { success, data: WebhookEventResponse, timestamp }
	 */
	async handleWebhook(payload: unknown) {
		return this.execute('webhook', { body: payload });
	}

	/**
	 * Get pricing information for different analysis types
	 * GET /payment/pricing
	 * @returns Promise<GetPricingApiResponse> - Wrapped response: { success, data: GetPricingResponse, timestamp }
	 */
	async getPricing() {
		return this.execute('pricing', {});
	}
}
